import React from 'react';
import { Check, Type } from 'lucide-react';
import { formatCaseValue, parseMergeTag } from '../../lib/variables/resolver';

export type CaseFormat = 'original' | 'upper' | 'lower' | 'sentence' | 'title' | 'capitalize' | 'toggle';

export const CASE_FORMATS: { key: CaseFormat; label: string; example: string }[] = [
  { key: 'original', label: 'Original', example: 'As stored' },
  { key: 'upper', label: 'UPPERCASE', example: 'JOHN' },
  { key: 'lower', label: 'lowercase', example: 'john' },
  { key: 'sentence', label: 'Sentence case', example: 'John' },
  { key: 'title', label: 'Title Case', example: 'John Michael' },
  { key: 'capitalize', label: 'Capitalize Each Word', example: 'John Michael' },
  { key: 'toggle', label: 'tOGGLE cASE', example: 'jOHN' },
];

interface CaseFormatSelectorProps {
  selected: CaseFormat;
  onChange: (format: CaseFormat) => void;
  onClose?: () => void;
}

export default function CaseFormatSelector({ selected, onChange, onClose }: CaseFormatSelectorProps) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-xl p-1.5 min-w-[200px]">
      <div className="px-3 py-1.5 text-caption font-bold text-slate-400 uppercase tracking-wider flex items-center space-x-1.5">
        <Type className="h-3 w-3" />
        <span>Change Case</span>
      </div>
      <div className="mt-0.5 space-y-0.5">
        {CASE_FORMATS.map(({ key, label, example }) => (
          <button
            key={key}
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              onChange(key);
              onClose?.();
            }}
            className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-all flex items-center justify-between group ${
              selected === key
                ? 'bg-indigo-50 text-indigo-700'
                : 'hover:bg-slate-50 text-slate-700'
            }`}
          >
            <div>
              <span className="font-semibold">{label}</span>
              <span className="ml-2 text-caption text-slate-400">{example}</span>
            </div>
            {selected === key && <Check className="h-3.5 w-3.5 text-indigo-600" />}
          </button>
        ))}
      </div>
    </div>
  );
}

// Re-export centralized implementations
export { formatCaseValue, parseMergeTag };

/**
 * Given a merge field tag (e.g. "first_name") and a format, returns the
 * formatted merge field string for use in templates.
 */
export function buildMergeTag(fieldName: string, format: CaseFormat): string {
  if (!format || format === 'original') return `{{${fieldName}}}`;
  return `{{${fieldName}:${format}}}`;
}
